/**
 * useChatSend — extracted from ConversationsChat.js (Session 2 refactor).
 * Jul 2026
 *
 * Owns the outbound-message pipeline previously inlined in the chat page:
 *   1. Validate + append the teen's turn to the transcript
 *   2. Build the last-10-message history for context
 *   3. Fire /api/hibff-chat (Claude + guardrails)
 *   4. Passively analyse via `useEmpathicHandover.analyzeMessage`
 *   5. Compose the AvA reply (blocked / handover / nudge / raw)
 *   6. Voice-mode → TTS with word-reveal · Text-mode → 22 wps drip reveal
 *   7. Error path → visible "Failed to send" system message
 *
 * Behaviour is byte-identical to the pre-extraction path; this hook only
 * moves ~130 lines out of the component so the page can breathe. Passing
 * deps as an options object keeps the call-site readable and lets future
 * unit tests stub any dependency in isolation.
 */
import { useCallback } from 'react';
import { apiClient } from '../api/client';
import { devLog } from '../utils/devLogger';
import { CHALLENGE_TOKEN_RE } from '../utils/challengeTokens';

export const useChatSend = ({
  // Read-only state snapshots
  message,
  messages,
  currentSession,
  selectedAva,
  user,
  interactionMode,
  isMuted,
  sending,
  // Setters
  setMessage,
  setMessages,
  setSending,
  setRevealingMsgId,
  setRevealedWords,
  // Refs
  revealIntervalRef,
  // Handles
  playTTS,
  handover,
}) => {
  const handleSendMessage = useCallback(async (e, voiceText = null) => {
    if (e) e.preventDefault();
    const text = voiceText || message;

    // Input validation: prevent empty or whitespace-only messages
    if (!text || !text.trim() || text.trim().length === 0) {
      devLog('⚠️ Blocked empty message');
      return;
    }

    if (!currentSession || sending) {
      devLog('⚠️ No session or already sending');
      return;
    }

    const userMsg = {
      id: `temp_${Date.now()}`,
      role: 'user',
      content: text.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setMessage('');
    setSending(true);

    try {
      // Build history from recent messages for context
      const recentHistory = messages.slice(-10).map(m => ({
        role: m.role === 'user' ? 'user' : 'assistant',
        content: m.content,
      }));

      // CEOE: analyze the message through the empathic handover pipeline
      // IN PARALLEL with the chat call (it only needs the user's text) —
      // sequentially it added ~1.5s before the first word appeared.
      const avaName = selectedAva?.name || 'AvA';
      const analysisPromise = handover
        .analyzeMessage(text, avaName, currentSession?.session_id)
        .catch(() => null);

      // Call HiBFF Chat (Claude + guardrails)
      const response = await apiClient.hibffChat(
        user?.user_id || 'anonymous',
        selectedAva?.ava_id || selectedAva?.name || 'default',
        'practice',
        text,
        recentHistory,
        selectedAva?.avatar_config?.language || selectedAva?.language || 'en',
        selectedAva?.name,
        currentSession?.session_id || null,
      );

      const analysis = await analysisPromise;

      // Force-open the 3-step HandoverConsentModal whenever the chat
      // endpoint flagged a blocking path that requires the unified
      // handover UX. Guarantees parity even if /analyze hasn't returned
      // or returned handover_triggered=false. This is the single
      // handover UX across all triggering instances.
      if (response.triggerHandover) {
        handover.forceOpenConsent();
      }

      // Build AvA response
      let avaContent = response.reply;
      if (response.blocked) {
        avaContent = response.reply;
      } else if (analysis?.handover_triggered && !response.triggerHandover) {
        // Dec 2026 (warm handover paused): never promise a human connection.
        // When the chat endpoint already returned its own Safety Net reply
        // (triggerHandover=true) keep it; otherwise use the helpline copy.
        const lang = selectedAva?.avatar_config?.language || selectedAva?.language || 'en';
        if (lang === 'fr') {
          avaContent = `Je t'entends, et ce que tu ressens compte vraiment. Tu n'as pas besoin de traverser ça seul(e). Je viens d'ouvrir une fenêtre avec des lignes d'écoute gratuites — de vraies personnes bienveillantes, prêtes à t'écouter. Demander de l'aide est un geste courageux.`;
        } else if (lang === 'es') {
          avaContent = `Te escucho, y lo que sientes importa de verdad. No tienes que pasar por esto solo/a. Acabo de abrir una ventana con líneas de ayuda gratuitas — personas reales y amables, listas para escucharte. Pedir ayuda es un acto de valentía.`;
        } else {
          avaContent = `I hear you, and what you're feeling truly matters. You don't have to go through this alone. I've opened a window with free helplines — kind, real people who understand and are ready to listen. Asking for help is a brave step.`;
        }
      } else if (analysis?.nudge_text && analysis.nudge_level >= 2) {
        avaContent = response.reply + '\n\n' + analysis.nudge_text;
      }

      const avaMsg = {
        id: `ava_${Date.now()}`,
        role: 'ava',
        content: avaContent,
        timestamp: new Date(),
        blocked: response.blocked,
        blockReason: response.blockReason,
        nudgeLevel: analysis?.nudge_level || 0,
      };
      if (interactionMode !== 'text' && !isMuted) {
        setRevealingMsgId(avaMsg.id);
        setRevealedWords(0);
        setMessages(prev => [...prev, avaMsg]);
        await playTTS(avaContent, avaMsg.id);
      } else {
        // Text-only or muted — append the message and drip the words in
        // at ~22 wps so the response flows visually instead of slamming
        // in as a block. Mirrors the TTS-synced reveal behaviour.
        setMessages(prev => [...prev, avaMsg]);
        setRevealingMsgId(avaMsg.id);
        setRevealedWords(0);
        const cleanContent = (avaContent || '').replace(CHALLENGE_TOKEN_RE, '').trim();
        const totalWords = cleanContent.split(/\s+/).filter(Boolean).length;
        if (revealIntervalRef.current) clearInterval(revealIntervalRef.current);
        let shown = 0;
        revealIntervalRef.current = setInterval(() => {
          shown += 1;
          setRevealedWords(shown);
          if (shown >= totalWords) {
            clearInterval(revealIntervalRef.current);
            revealIntervalRef.current = null;
            setRevealingMsgId(null);
            setRevealedWords(9999);
          }
        }, 45);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        role: 'system',
        content: 'Failed to send message. Please try again.',
        timestamp: new Date(),
      }]);
    } finally {
      setSending(false);
    }
  }, [
    message, messages, currentSession, selectedAva, user,
    interactionMode, isMuted, sending,
    setMessage, setMessages, setSending, setRevealingMsgId, setRevealedWords,
    revealIntervalRef, playTTS, handover,
  ]);

  return { handleSendMessage };
};

export default useChatSend;
